// The carrier catalog itself — codes, names, the hints that recognise a carrier
// by name, and which portals we can read — comes from the backend
// (insurance-company/carriers). What lives here is only what the browser owns:
// each carrier's mark, and the name matching that suggests a carrier in the
// Add/Edit form. A record's carrier is whatever was saved on it, never guessed
// from its name at display time.

const ICONS = '/img/renova-dashboard/icons/carriers';

/**
 * The carriers that have a mark in public/ — keep in step with that folder. A code not
 * listed (Handle, which is not a carrier but reads several) gets the neutral placeholder
 * rather than a broken image.
 */
const MARKS = new Set([
  'afirme_mx',
  'aig_mx',
  'allianz_mx',
  'ana_mx',
  'argos_mx',
  'atlas_mx',
  'axa_mx',
  'banorte_mx',
  'bupa_mx',
  'chubb_mx',
  'continental_assist_mx',
  'gnp_mx',
  'hdi_mx',
  'inbursa_mx',
  'mapfre_mx',
  'planseguro_mx',
  'qualitas_mx',
  'santander_mx',
  'skandia_mx',
  'sura_mx',
  'zurich_mx',
]);

/** The placeholder shown for a carrier with no mark of its own. */
export const CARRIER_PLACEHOLDER = `${ICONS}/placeholder.svg`;

/** Whether the carrier has a mark of its own (the placeholder is not one to save as a logo). */
export function hasCarrierMark(code) {
  return Boolean(code) && MARKS.has(code);
}

/** A carrier's mark, served from public/, by its code; the placeholder for one without; null without a code. */
export function carrierIcon(code) {
  if (!code) return null;
  return hasCarrierMark(code) ? `${ICONS}/${code}.png` : CARRIER_PLACEHOLDER;
}

/**
 * The logo for a carrier's read, or null for the placeholder: the company's own saved logo first,
 * then the mark of the portal its account reads.
 *
 * A run names neither its carrier's code nor its portal, so both are looked up — the company by
 * `insuranceCompanyId` among `companies` (insurance-company/list), the portal through the account
 * by `scrapeAccountId` among `accounts` (scrape/account/list), falling back to any account of the
 * same carrier.
 */
export function carrierLogoFor({ insuranceCompanyId, scrapeAccountId } = {}, { companies, accounts } = {}) {
  const company = insuranceCompanyId
    ? (companies || []).find((c) => c.insuranceCompanyId === insuranceCompanyId)
    : null;
  if (company && company.logoUrl) return company.logoUrl;
  const all = accounts || [];
  const account = (scrapeAccountId && all.find((a) => a.scrapeAccountId === scrapeAccountId))
    || (insuranceCompanyId && all.find((a) => a.insuranceCompanyId === insuranceCompanyId));
  return (account && carrierIcon(account.portal)) || null;
}

/** Lower case, accents and punctuation gone, single spaces: "Quálitas, S.A." -> "qualitas s a". */
function normalise(text) {
  return (text || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

/**
 * The catalog carrier a carrier name refers to, or null — a suggestion only.
 *
 * Whole words against each entry's `carrierNameHints` — padded with spaces so
 * "hdi" does not match inside another word — and the first entry wins when a
 * name mentions two.
 */
export function suggestCarrier(name, catalog) {
  const padded = ` ${normalise(name)} `;
  if (!padded.trim()) return null;
  return (catalog || []).find(
    (c) => (c.carrierNameHints || []).some((h) => normalise(h) && padded.includes(` ${normalise(h)} `)),
  ) || null;
}
